// The person's Queravel chat. Rhema keeps the history there as ordinary encrypted messages, each
// marked with the Queravel conversation it belongs to. Queravel itself keeps nothing.

import type { ChatMessage } from "../client";
import type { QueravelSource } from "../content";
import { newQueravelChatId, type QueravelTurn } from "./queravel";

/** The Queravel conversation a stored message belongs to, if it belongs to one. */
export function queravelChatOf(m: ChatMessage): string | null {
  const c = m.content;
  if (!c || m.deleted) return null;
  if (c.type === "queravel") return c.chat_id;
  if (c.type === "text" && c.queravel) return c.queravel.chat_id;
  return null;
}

/** The conversation the newest stored turn belongs to, or null when nothing is stored yet. */
export function latestQueravelChat(messages: ChatMessage[]): string | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const id = queravelChatOf(messages[i] as ChatMessage);
    if (id) return id;
  }
  return null;
}

/** The conversation the newest stored turn belongs to, or a new one when there is none. */
export function currentQueravelChat(messages: ChatMessage[]): string {
  return latestQueravelChat(messages) ?? newQueravelChatId();
}

/**
 * The turns of one Queravel conversation, oldest first, rebuilt from the stored messages. The
 * person's questions are text messages and Queravel's answers have their own content type.
 */
export function assistantTurns(messages: ChatMessage[], chatId: string): QueravelTurn[] {
  const turns: QueravelTurn[] = [];
  for (const m of messages) {
    if (queravelChatOf(m) !== chatId) continue;
    const c = m.content;
    if (c?.type === "queravel") turns.push({ role: "assistant", text: c.body });
    else if (c?.type === "text") turns.push({ role: "user", text: m.edited ?? c.body });
  }
  return turns;
}

/** The name a person gave a Queravel conversation, when the stored message is one. */
export function queravelTitleOf(m: ChatMessage): { conversation: string; title: string } | null {
  const c = m.content;
  if (!c || m.deleted || c.type !== "queravel_title") return null;
  return { conversation: c.conversation, title: c.title };
}

/** One Queravel conversation as the conversation list shows it. */
export type QueravelConversation = {
  id: string;
  /** The person's own name for it, or null when they have not named it. */
  title: string | null;
  /** The first question, or its short label when it has one. */
  firstQuestion: string;
  /** The newest question or answer. */
  lastText: string;
  /** When the newest question or answer was stored. */
  lastAt: string;
  /** The chat its first question was about, or null when it was about nothing in Rhema. */
  source: QueravelSource | null;
};

/** Cuts text to one line of at most `max` characters, at a word where it can. */
export function shortenQuestion(text: string, max = 48): string {
  const line = text.replace(/\s+/g, " ").trim();
  if (line.length <= max) return line;
  const cut = line.slice(0, max - 1);
  const space = cut.lastIndexOf(" ");
  return `${(space > max / 2 ? cut.slice(0, space) : cut).trimEnd()}…`;
}

/** What a conversation is called: the person's name for it, or else its first question shortened. */
export function queravelConversationName(c: Pick<QueravelConversation, "title" | "firstQuestion">): string {
  return c.title || shortenQuestion(c.firstQuestion) || "New conversation";
}

/** What one stored turn says, as a list shows it. */
function turnText(m: ChatMessage): string {
  const c = m.content;
  if (c?.type === "text") return c.queravel?.label ?? m.edited ?? c.body;
  if (c?.type === "queravel") return c.body;
  return "";
}

/**
 * Every Queravel conversation with at least one stored question or answer, newest first. The
 * messages come in chat order, so the conversation with the latest turn in the chat is newest
 * whatever the device clocks said. The newest title message for a conversation names it.
 */
export function queravelConversations(messages: ChatMessage[]): QueravelConversation[] {
  const byId = new Map<string, QueravelConversation>();
  const titles = new Map<string, string>();
  for (const m of messages) {
    const named = queravelTitleOf(m);
    if (named) {
      titles.set(named.conversation, named.title.trim());
      continue;
    }
    const id = queravelChatOf(m);
    if (!id) continue;
    const text = turnText(m);
    const question = m.content?.type === "text" ? text : "";
    const source = m.content?.type === "text" ? (m.content.queravel?.source ?? null) : null;
    const known = byId.get(id);
    // Taken out and put back, so the map ends in the order of each conversation's latest turn.
    byId.delete(id);
    byId.set(id, {
      id,
      title: null,
      firstQuestion: known?.firstQuestion || question,
      lastText: text,
      lastAt: m.createdAt,
      source: known?.source ?? source,
    });
  }
  for (const [id, title] of titles) {
    const c = byId.get(id);
    if (c) c.title = title || null;
  }
  return [...byId.values()].reverse();
}

/** The person's name for one conversation, or null when they have not named it. */
export function queravelTitle(messages: ChatMessage[], conversation: string): string | null {
  let title: string | null = null;
  for (const m of messages) {
    const named = queravelTitleOf(m);
    if (named?.conversation === conversation) title = named.title.trim() || null;
  }
  return title;
}

/** Every stored message of one conversation, its titles included, for deleting it. */
export function queravelConversationMessages(messages: ChatMessage[], conversation: string): string[] {
  return messages
    .filter((m) => queravelChatOf(m) === conversation || queravelTitleOf(m)?.conversation === conversation)
    .map((m) => m.id);
}
